import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { apiClient } from '@/common/lib';
import { env } from '@/common/config';

export function useAttachmentDownload() {
  const [downloadingIds, setDownloadingIds] = useState(new Set());

  const downloadAttachment = useCallback(
    async (attachment) => {
      if (!attachment || downloadingIds.has(attachment.id)) return;

      // For link types just open them
      if (
        attachment.attachment_type === 'link' ||
        attachment.attachment_type === 'external_link'
      ) {
        if (attachment.file_url) {
          window.open(attachment.file_url, '_blank', 'noopener,noreferrer');
        }
        return;
      }

      const fileName = attachment.file_name || 'download';
      const rawUrl = attachment.file_url;

      if (!rawUrl) {
        toast.error('No file URL available.');
        return;
      }

      const url = rawUrl.startsWith('http')
        ? rawUrl
        : `${env.photoBaseUrl}${rawUrl}`;

      setDownloadingIds((prev) => new Set(prev).add(attachment.id));
      const toastId = toast.loading(`Downloading ${fileName}...`);

      try {
        // For absolute URLs (e.g., S3/CDN links), fetch directly
        // apiClient handles auth token injection
        const response = await apiClient.get(url, { responseType: 'blob' });

        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'application/octet-stream',
        });
        const blobUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);

        toast.success('Download complete', { id: toastId });
      } catch (error) {
        console.error('Failed to download attachment:', error);
        toast.error('Failed to download file', { id: toastId });
      } finally {
        setDownloadingIds((prev) => {
          const next = new Set(prev);
          next.delete(attachment.id);
          return next;
        });
      }
    },
    [downloadingIds],
  );

  return { downloadAttachment, downloadingIds };
}
